// NEW_FILE_JUSTIFIED: Encapsulates insurance type filtering and template matching logic for CustomerApplyPage — extracted to keep page under 200 lines
import { useMemo, useState } from 'react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hook in src/hooks/ requires two levels up to reach convex/_generated/
import type { IPolicyFormTemplate } from '@/@types/policyFormTemplates';
import type { IOrgInsuranceType } from '@/@types/insurance';

export interface IInsuranceTypeConfig {
  iconName: string;
  iconColor: string;
  iconBgColor: string;
  description: string;
  features: string[];
  isPopular?: boolean;
}

export const INSURANCE_TYPE_CONFIG: Record<string, IInsuranceTypeConfig> = {
  'Health Insurance': {
    iconName: 'heart-pulse',
    iconColor: '#84CC16',
    iconBgColor: '#F0FDF4',
    description:
      'Comprehensive medical coverage for you and your family, including hospital stays, specialist visits, and preventative care.',
    features: ['Worldwide coverage', 'Family & dependants included', 'Dental & optical benefits'],
    isPopular: true,
  },
  'Car Insurance': {
    iconName: 'car',
    iconColor: '#3B82F6',
    iconBgColor: '#EFF6FF',
    description:
      'Protect your vehicle against accidents, theft, and third-party liability with flexible cover options.',
    features: [
      'Comprehensive & third-party',
      'Named drivers support',
      'Roadside assistance included',
    ],
  },
  'Travel Insurance': {
    iconName: 'plane',
    iconColor: '#F97316',
    iconBgColor: '#FFF7ED',
    description:
      'Coverage for trips abroad including medical emergencies, trip cancellation, and lost luggage protection.',
    features: [
      'Medical emergency abroad',
      'Trip cancellation protection',
      'Family coverage available',
    ],
  },
  'Home Insurance': {
    iconName: 'house',
    iconColor: '#7C3AED',
    iconBgColor: '#EDE9FE',
    description:
      'Protect your property and belongings against damage, theft, and unforeseen events.',
    features: [
      'Building & contents cover',
      'Natural disaster protection',
      'Liability coverage included',
    ],
  },
};

const DEFAULT_CONFIG: IInsuranceTypeConfig = {
  iconName: 'shield',
  iconColor: '#84CC16',
  iconBgColor: '#F0FDF4',
  description: 'Comprehensive insurance coverage tailored to your needs.',
  features: ['Flexible coverage options', 'Fast claims processing', 'Dedicated support'],
};

export function getInsuranceTypeConfig(name: string): IInsuranceTypeConfig {
  return INSURANCE_TYPE_CONFIG[name] ?? DEFAULT_CONFIG;
}

interface IUseInsuranceTypeSelectionParams {
  orgTypes: IOrgInsuranceType[] | undefined;
  templates: IPolicyFormTemplate[] | undefined;
  policySource: 'personal' | 'company_employer';
  allowedInsuranceTypeIds: Id<'orgInsuranceTypes'>[] | undefined;
  selectedInsuranceTypeId: Id<'orgInsuranceTypes'> | null;
}

export interface IUseInsuranceTypeSelectionResult {
  visibleOrgTypes: IOrgInsuranceType[];
  filteredOrgTypes: IOrgInsuranceType[];
  uniqueTypeNames: string[];
  filterType: string;
  setFilterType: (type: string) => void;
  hasExplicitEmptyAllowList: boolean;
  templatesForSelectedType: IPolicyFormTemplate[];
  autoSelectedTemplate: IPolicyFormTemplate | null;
}

export function useInsuranceTypeSelection({
  orgTypes,
  templates,
  policySource,
  allowedInsuranceTypeIds,
  selectedInsuranceTypeId,
}: IUseInsuranceTypeSelectionParams): IUseInsuranceTypeSelectionResult {
  const [filterType, setFilterType] = useState<string>('all');

  const computed = useMemo(() => {
    const allTypes = orgTypes ?? [];
    const allTemplates = templates ?? [];

    // Company employers with an explicit empty allowedInsuranceTypeIds array see nothing
    const hasExplicitEmptyAllowList =
      policySource === 'company_employer' &&
      Array.isArray(allowedInsuranceTypeIds) &&
      allowedInsuranceTypeIds.length === 0;

    let visibleOrgTypes: IOrgInsuranceType[];
    if (
      policySource === 'company_employer' &&
      Array.isArray(allowedInsuranceTypeIds) &&
      allowedInsuranceTypeIds.length > 0
    ) {
      visibleOrgTypes = allTypes.filter(t => allowedInsuranceTypeIds.includes(t._id));
    } else if (hasExplicitEmptyAllowList) {
      visibleOrgTypes = [];
    } else {
      visibleOrgTypes = allTypes;
    }

    const uniqueTypeNames = visibleOrgTypes.map(t => t.name);

    const filteredOrgTypes =
      filterType === 'all' ? visibleOrgTypes : visibleOrgTypes.filter(t => t.name === filterType);

    const templatesForSelectedType = selectedInsuranceTypeId
      ? allTemplates.filter(
          t => t.policySource === policySource && t.insuranceTypeId === selectedInsuranceTypeId
        )
      : [];

    const autoSelectedTemplate =
      templatesForSelectedType.length === 1 ? templatesForSelectedType[0] : null;

    return {
      visibleOrgTypes,
      filteredOrgTypes,
      uniqueTypeNames,
      hasExplicitEmptyAllowList,
      templatesForSelectedType,
      autoSelectedTemplate,
    };
  }, [
    orgTypes,
    templates,
    policySource,
    allowedInsuranceTypeIds,
    selectedInsuranceTypeId,
    filterType,
  ]);

  return { ...computed, filterType, setFilterType };
}
